import React, { useRef, useState, useMemo, useCallback } from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';

/**
 * 统一的视频播放器组件
 * - 未播放时显示 poster + 三角形播放按钮
 * - 播放时整个卡片变成 fixed 全屏覆盖（放大）
 * - 放大状态下显示控制栏：暂停/继续、关闭、进度条
 * - 点击关闭或播放结束恢复原位
 *
 * Props:
 *   src: 视频地址
 *   poster: 封面图
 *   startTime / endTime: 可选，截取播放时间段
 */
function VideoPlayer({ src, poster, startTime, endTime }) {
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // 动态计算有效起止时间：endTime 未传时用 duration，duration 还没加载时用 Infinity
  const safeStart = startTime || 0;
  const safeEnd = useMemo(() => endTime || duration || Infinity, [endTime, duration]);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = safeStart;
    video.play().catch(() => {});
    setPlaying(true);
    setPaused(false);
  }, [safeStart]);

  const handleVideoClick = useCallback((e) => {
    if (playing) {
      e.stopPropagation();
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        video.play();
        setPaused(false);
      } else {
        video.pause();
        setPaused(true);
      }
    }
  }, [playing]);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = safeStart;
    }
    setPlaying(false);
    setPaused(false);
    setProgress(0);
  }, [safeStart]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setProgress(video.currentTime);
    if (video.currentTime >= safeEnd) {
      video.pause();
      video.currentTime = safeStart;
      setPlaying(false);
      setPaused(false);
      setProgress(0);
    }
  }, [safeEnd, safeStart]);

  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (video) video.currentTime = safeStart;
    setPlaying(false);
    setPaused(false);
    setProgress(0);
  }, [safeStart]);

  // 组件卸载时自动暂停视频，防止切页后继续播放
  React.useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) setDuration(video.duration);
  }, []);

  const handleSeek = useCallback((e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const newTime = Math.max(safeStart, Math.min(safeEnd, ratio * duration));
    video.currentTime = newTime;
    setProgress(newTime);
  }, [duration, safeStart, safeEnd]);

  // 进度百分比
  const effectiveEnd = endTime || duration || 1; // 避免除以0或Infinity
  const progressPercent = duration > 0
    ? ((progress - safeStart) / (effectiveEnd - safeStart)) * 100
    : 0;
  const clampedPercent = Math.max(0, Math.min(100, progressPercent));

  const formatTime = (t) => {
    if (!isFinite(t) || t < 0) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={`phonics-video-card ${playing ? 'phonics-video-card-expanded' : ''}`}
      onClick={playing ? undefined : handlePlay}
    >
      <div className="phonics-video-container">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => { setPlaying(true); setPaused(false); }}
          onPause={() => { if (playing) setPaused(true); }}
          onClick={handleVideoClick}
          playsInline
          preload="metadata"
        />
        {/* 播放前覆盖层：三角形按钮 */}
        {!playing && (
          <div className="phonics-video-overlay">
            <div className="phonics-play-btn">
              <PlayArrowIcon sx={{ fontSize: 36 }} />
            </div>
          </div>
        )}
      </div>

      {/* 放大状态下的控制栏 */}
      {playing && (
        <div className="phonics-video-controls" onClick={(e) => e.stopPropagation()}>
          <div className="phonics-video-progress-bar" onClick={handleSeek}>
            <div
              className="phonics-video-progress-fill"
              style={{ width: `${clampedPercent}%` }}
            />
          </div>
          <div className="phonics-video-controls-row">
            <button
              className="phonics-video-ctrl-btn"
              onClick={(e) => {
                e.stopPropagation();
                const video = videoRef.current;
                if (!video) return;
                if (video.paused) { video.play(); setPaused(false); }
                else { video.pause(); setPaused(true); }
              }}
            >
              {paused ? <PlayArrowIcon sx={{ fontSize: 22 }} /> : <PauseIcon sx={{ fontSize: 22 }} />}
            </button>
            <span className="phonics-video-time">
              {formatTime(progress - safeStart)} / {formatTime(effectiveEnd - safeStart)}
            </span>
            <button className="phonics-video-ctrl-btn" onClick={handleClose}>
              <CloseIcon sx={{ fontSize: 22 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoPlayer;
